import React from 'react';
import '../styles/LeadsFilterModal.css';
import { Input, SearchInput, PredictiveSearch, FilterChip } from '../components';
import { numberFormatter } from '../styles/GlobalStyles';

function LeadsFilterModal(props) {
    const priceMin = props.priceMin;
    const setPriceMin = props.setPriceMin;
    const priceMax = props.priceMax;
    const setPriceMax = props.setPriceMax;
    const filterState = props.filterState;
    const setFilterState = props.setFilterState;
    const filterCity = props.filterCity;
    const setFilterCity = props.setFilterCity;
    const bedsMin = props.bedsMin;
    const setBedsMin = props.setBedsMin;
    const bedsMax = props.bedsMax;
    const setBedsMax = props.setBedsMax;
    const bathsMin = props.bathsMin;
    const setBathsMin = props.setBathsMin;
    const bathsMax = props.bathsMax;
    const setBathsMax = props.setBathsMax;
    const sqftMin = props.sqftMin;
    const setSqftMin = props.setSqftMin;
    const sqftMax = props.sqftMax;
    const setSqftMax = props.setSqftMax;
    const yearBuiltMin = props.yearBuiltMin;
    const setYearBuiltMin = props.setYearBuiltMin;
    const yearBuiltMax = props.yearBuiltMax;
    const setYearBuiltMax = props.setYearBuiltMax;
    const availableStates = props.availableStates;
    const availableCities = props.availableCities;
    const onSelectState = props.onSelectState;
    const onSelectCity = props.onSelectCity;
    const onClearState = props.onClearState;
    const onClearCity = props.onClearCity;
    const spreadMin = props.spreadMin;
    const setSpreadMin = props.setSpreadMin;
    const maoVsAskingMax = props.maoVsAskingMax;
    const setMaoVsAskingMax = props.setMaoVsAskingMax;
    // Co-living financing filters (functions/utils/coLivingCashOutRank.js). `availableFinancingPaths`
    // is the set of paths the company's leads ACTUALLY carry, so an operator is never offered a
    // "DSCR purchase" chip that can only ever return nothing.
    const financingPathOptions = props.financingPathOptions || [];
    const availableFinancingPaths = props.availableFinancingPaths || [];
    const selectedFinancingPaths = props.selectedFinancingPaths || [];
    const onToggleFinancingPath = props.onToggleFinancingPath;
    const dealScoreMin = props.dealScoreMin;
    const setDealScoreMin = props.setDealScoreMin;
    // Offer a path chip when the company has deals on it OR it's already selected (so an applied
    // filter is always clearable, even if the last lead on that path has since been archived).
    const financingPathChips = financingPathOptions.filter(
        (o) => availableFinancingPaths.includes(o.value) || selectedFinancingPaths.includes(o.value)
    );
    // The deal score is computed ONLY on the hard-money → refi path (coLivingCashOutRank.js
    // returns early with `not_bridge_path` otherwise), so every other lead carries null — and
    // getOrderedDocs' range filter EXCLUDES nulls rather than reading them as 0. A score floor
    // combined with a selection that leaves the bridge path out is therefore a guaranteed empty
    // table, with nothing on screen to explain it. So the input needs the bridge path to be in
    // play: present in the company's data, and not excluded by the current selection.
    //
    // `|| !!dealScoreMin` keeps an already-set value visible so it stays clearable — Leads.js
    // empties the draft when a selection drops the bridge path, so through the UI this only
    // fires for a hand-edited URL.
    const bridgeInData = availableFinancingPaths.includes('bridgeRefi')
        || selectedFinancingPaths.includes('bridgeRefi');
    const bridgeExcluded = selectedFinancingPaths.length > 0
        && !selectedFinancingPaths.includes('bridgeRefi');
    const showDealScoreInput = (bridgeInData && !bridgeExcluded) || !!dealScoreMin;
    const showFinancingSection = financingPathChips.length > 0 || showDealScoreInput;
    const dateMin = props.dateMin;
    const setDateMin = props.setDateMin;
    const dateMax = props.dateMax;
    const setDateMax = props.setDateMax;
    const selectedPropertyTypes = props.selectedPropertyTypes || [];
    const availablePropertyTypes = props.availablePropertyTypes || [];
    const onTogglePropertyType = props.onTogglePropertyType;
    const selectedAssignees = props.selectedAssignees || [];
    const availableAssignees = props.availableAssignees || [];
    const currentUserId = props.currentUserId;
    const onToggleAssignee = props.onToggleAssignee;
    const selectedDealStatuses = props.selectedDealStatuses || [];
    const onToggleDealStatus = props.onToggleDealStatus;
    const dealStatusOptions = props.dealStatusOptions || [];
    // Auction (foreclosure / trustee-sale) filters — Tier 1.
    const auctionStatusOptions = props.auctionStatusOptions || [];
    const lienPositionOptions = props.lienPositionOptions || [];
    const availableAuctionStatuses = props.availableAuctionStatuses || [];
    const selectedAuctionStatuses = props.selectedAuctionStatuses || [];
    const onToggleAuctionStatus = props.onToggleAuctionStatus;
    const selectedLienPositions = props.selectedLienPositions || [];
    const onToggleLienPosition = props.onToggleLienPosition;
    const auctionDateMin = props.auctionDateMin;
    const setAuctionDateMin = props.setAuctionDateMin;
    const auctionDateMax = props.auctionDateMax;
    const setAuctionDateMax = props.setAuctionDateMax;
    // Show the Auction section only when the company actually has auction leads,
    // or an auction filter is already applied (so its chip can still be cleared).
    const showAuctionSection = availableAuctionStatuses.length > 0
        || selectedAuctionStatuses.length > 0
        || selectedLienPositions.length > 0
        || !!auctionDateMin
        || !!auctionDateMax;
    const selectedSenders = props.selectedSenders || [];
    const availableSenders = props.availableSenders || [];
    const senderSearch = props.senderSearch || '';
    const onSenderSearchChange = props.onSenderSearchChange;
    const onClearSenderSearch = props.onClearSenderSearch;
    const filteredSenderResults = props.filteredSenderResults || [];
    const onSelectSender = props.onSelectSender;
    const onRemoveSender = props.onRemoveSender;
    const availablePortfolios = props.availablePortfolios || [];
    const selectedPortfolios = props.selectedPortfolios || [];
    const onTogglePortfolio = props.onTogglePortfolio;
    const archivedView = props.archivedView === true;
    const setArchivedView = props.setArchivedView;

    const formatCurrencyInput = (val) => {
        const cleaned = String(val).replace(/[^0-9]/g, '');
        if (cleaned === '') return '';
        return numberFormatter.format(parseInt(cleaned, 10));
    };

    const handlePriceChange = (setter) => (e) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, '');
        setter(cleaned === '' ? '' : formatCurrencyInput(cleaned));
    };

    const renderRangeRow = (label, minVal, setMin, maxVal, setMax, placeholder, leftUnit) => (
        <div className="leads-filter-labeled-row">
            <span className="body-regular colour-secondary leads-filter-field-label">
                {label}
            </span>
            <div className="leads-filter-range-row">
                <Input
                    label={null}
                    labelType={null}
                    info={false}
                    value={minVal}
                    onChange={(e) => setMin(e.target.value)}
                    placeholder={placeholder || "Min"}
                    type="text"
                    leftUnit={leftUnit || null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={false}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                <span className="body-regular colour-secondary leads-filter-dash">–</span>
                <Input
                    label={null}
                    labelType={null}
                    info={false}
                    value={maxVal}
                    onChange={(e) => setMax(e.target.value)}
                    placeholder={placeholder ? placeholder.replace('Min', 'Max') : "Max"}
                    type="text"
                    leftUnit={leftUnit || null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={false}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
            </div>
        </div>
    );

    return (
        <div className="leads-filter-modal-body">
            {/* Status Section — flips between active leads (default) and the
                archived pool. Sits at the top because it determines which
                universe every other filter narrows: a price range on archived
                leads is a different result than the same price range on
                active leads. Mutually exclusive with the lead-type chip in
                the page header (handled by the apply handler in Leads.js). */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Status
                </span>
                <div className="leads-filter-chip-row">
                    <FilterChip
                        label={archivedView ? 'Viewing archived leads' : 'Show archived leads'}
                        selected={archivedView}
                        index="archived"
                        disabled={false}
                        func={() => setArchivedView && setArchivedView(!archivedView)}
                        leftIcon={null}
                        rightIcon={null}
                    />
                </div>
            </div>

            <div className="light-grey-divider"></div>

            {/* Price Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Price
                </span>
                <div className="leads-filter-row">
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={priceMin}
                        onChange={handlePriceChange(setPriceMin)}
                        placeholder="Min"
                        type="text"
                        leftUnit={"$"}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <span className="body-regular colour-secondary leads-filter-dash">–</span>
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={priceMax}
                        onChange={handlePriceChange(setPriceMax)}
                        placeholder="Max"
                        type="text"
                        leftUnit={"$"}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            <div className="light-grey-divider"></div>

            {/* Location Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Location
                </span>
                <div className="leads-filter-row">
                    <div className="leads-filter-search-container relative-container">
                        <SearchInput
                            value={filterState}
                            onChange={(e) => setFilterState(e.target.value)}
                            placeholder="State (e.g. TX)"
                            clearInput={onClearState}
                        />
                        {filterState !== "" && availableStates.length > 0 &&
                            <PredictiveSearch
                                loading={false}
                                results={availableStates}
                                selectItem={onSelectState}
                                nested={false}
                            />
                        }
                    </div>
                    <div className="leads-filter-search-container relative-container">
                        <SearchInput
                            value={filterCity}
                            onChange={(e) => setFilterCity(e.target.value)}
                            placeholder="City"
                            clearInput={onClearCity}
                        />
                        {filterCity !== "" && availableCities.length > 0 &&
                            <PredictiveSearch
                                loading={false}
                                results={availableCities}
                                selectItem={onSelectCity}
                                nested={false}
                            />
                        }
                    </div>
                </div>
            </div>

            {(availableSenders.length > 0 || selectedSenders.length > 0) && (
                <>
                    <div className="light-grey-divider"></div>

                    {/* Sender Section */}
                    <div className="leads-filter-section">
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Sender
                        </span>
                        <div className="leads-filter-search-container relative-container">
                            <SearchInput
                                value={senderSearch}
                                onChange={onSenderSearchChange}
                                placeholder="Search senders..."
                                clearInput={onClearSenderSearch}
                            />
                            {senderSearch !== "" && filteredSenderResults.length > 0 &&
                                <PredictiveSearch
                                    loading={false}
                                    results={filteredSenderResults}
                                    selectItem={onSelectSender}
                                    nested={false}
                                />
                            }
                        </div>
                        {selectedSenders.length > 0 && (
                            <div className="leads-filter-chip-row">
                                {selectedSenders.map(name => (
                                    <FilterChip
                                        key={name}
                                        label={name}
                                        selected={true}
                                        index={name}
                                        disabled={false}
                                        func={() => onRemoveSender(name)}
                                        leftIcon={null}
                                        rightIcon={null}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className="light-grey-divider"></div>

            {/* Spread Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Spread
                </span>
                <div className="leads-filter-row">
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={spreadMin}
                        onChange={(e) => setSpreadMin(e.target.value)}
                        placeholder="Min %"
                        type="text"
                        leftUnit={null}
                        rightUnit={"%"}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            {/* Financing Section — co-living only, and only for the paths this company's deals
                actually take. The chips narrow to HOW the deal would be bought: "Hard money →
                refi" is a house in bad enough shape that a DSCR lender won't write it as-is;
                "DSCR purchase" is one clean enough to finance from day one. Picking both still
                excludes leads with no path at all. The input underneath sets how close to the
                asking price the fully-recycling price has to be — 100% means "only deals that
                already work at the ask" — and is bridge-only, hence its own gate. */}
            {showFinancingSection && (
                <>
                    <div className="light-grey-divider"></div>

                    <div className="leads-filter-section">
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Financing
                        </span>
                        {financingPathChips.length > 0 && (
                            <div className="leads-filter-chip-row">
                                {financingPathChips.map((option) => (
                                    <FilterChip
                                        key={option.value}
                                        label={option.label}
                                        selected={selectedFinancingPaths.includes(option.value)}
                                        index={option.value}
                                        disabled={false}
                                        func={() => onToggleFinancingPath && onToggleFinancingPath(option.value)}
                                        leftIcon={null}
                                        rightIcon={null}
                                    />
                                ))}
                            </div>
                        )}
                        {showDealScoreInput && (
                            <div className="leads-filter-labeled-row">
                                <span className="body-regular colour-secondary leads-filter-field-label">
                                    Deal score
                                </span>
                                <div className="leads-filter-range-row">
                                    <Input
                                        label={null}
                                        labelType={null}
                                        info={false}
                                        value={dealScoreMin}
                                        onChange={(e) => setDealScoreMin(e.target.value)}
                                        placeholder="Min % of asking"
                                        type="text"
                                        leftUnit={null}
                                        rightUnit={"%"}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={false}
                                        multiline={false}
                                        error={false}
                                        onMouseEnter={null}
                                        onMouseLeave={null}
                                        reset={false}
                                        resetFunc={null}
                                        resetPosition={null}
                                        focusMultiline={false}
                                        prompt={false}
                                        submitPromptDisabled={false}
                                        submitPrompt={null}
                                        onHeightChange={null}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className="light-grey-divider"></div>

            {/* Our price vs Asking Section — the gap is OUR price's (utils/ourPrice.js), not the flip MAO's */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Max Our Price vs Asking
                </span>
                <div className="leads-filter-row">
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={maoVsAskingMax}
                        onChange={(e) => setMaoVsAskingMax(e.target.value)}
                        placeholder="Max % below asking"
                        type="text"
                        leftUnit={null}
                        rightUnit={"%"}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            <div className="light-grey-divider"></div>

            {/* Date Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Date
                </span>
                <div className="leads-filter-row">
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={dateMin}
                        onChange={(e) => setDateMin(e.target.value)}
                        placeholder="From"
                        type="date"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <span className="body-regular colour-secondary leads-filter-dash">–</span>
                    <Input
                        label={null}
                        labelType={null}
                        info={false}
                        value={dateMax}
                        onChange={(e) => setDateMax(e.target.value)}
                        placeholder="To"
                        type="date"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            {availableAssignees.length > 1 && (
                <>
                    <div className="light-grey-divider"></div>

                    {/* Assignee Section */}
                    <div className="leads-filter-section">
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Assignee
                        </span>
                        <div className="leads-filter-chip-row">
                            {availableAssignees.map(u => {
                                const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
                                const label = u.userId === currentUserId ? `${name} (Me)` : name;
                                return (
                                    <FilterChip
                                        key={u.userId}
                                        label={label}
                                        selected={selectedAssignees.includes(u.userId)}
                                        index={u.userId}
                                        disabled={false}
                                        func={() => onToggleAssignee(u.userId)}
                                        leftIcon={null}
                                        rightIcon={null}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            <div className="light-grey-divider"></div>

            {/* Deal Status Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Status
                </span>
                <div className="leads-filter-chip-row">
                    {dealStatusOptions.filter(opt => opt.value !== null).map(opt => (
                        <FilterChip
                            key={opt.value}
                            label={opt.label}
                            selected={selectedDealStatuses.includes(opt.value)}
                            index={opt.value}
                            disabled={false}
                            func={() => onToggleDealStatus(opt.value)}
                            leftIcon={null}
                            rightIcon={null}
                        />
                    ))}
                </div>
            </div>

            {(availablePortfolios.length > 0 || selectedPortfolios.length > 0) && (
                <>
                    <div className="light-grey-divider"></div>

                    {/* Portfolio Section — narrow the list to leads that belong
                        to one or more portfolios (packages). Chips are keyed by
                        portfolioId; the label shows the portfolio name. */}
                    <div className="leads-filter-section">
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Portfolio
                        </span>
                        <div className="leads-filter-chip-row">
                            {availablePortfolios.map(p => (
                                <FilterChip
                                    key={p.id}
                                    label={p.name}
                                    selected={selectedPortfolios.includes(p.id)}
                                    index={p.id}
                                    disabled={false}
                                    func={() => onTogglePortfolio(p.id)}
                                    leftIcon={null}
                                    rightIcon={null}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {showAuctionSection && (
                <>
                    <div className="light-grey-divider"></div>

                    {/* Auction Section — foreclosure / trustee-sale triage.
                        Status (is the sale real) and lien position (is the lien
                        safe) are the two go/no-go axes; auction date scopes the
                        sale-day window. */}
                    <div className="leads-filter-section">
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Auction status
                        </span>
                        <div className="leads-filter-chip-row">
                            {auctionStatusOptions.map(opt => (
                                <FilterChip
                                    key={opt.value}
                                    label={opt.label}
                                    selected={selectedAuctionStatuses.includes(opt.value)}
                                    index={opt.value}
                                    disabled={false}
                                    func={() => onToggleAuctionStatus(opt.value)}
                                    leftIcon={null}
                                    rightIcon={null}
                                />
                            ))}
                        </div>
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Lien position
                        </span>
                        <div className="leads-filter-chip-row">
                            {lienPositionOptions.map(opt => (
                                <FilterChip
                                    key={opt.value}
                                    label={opt.label}
                                    selected={selectedLienPositions.includes(opt.value)}
                                    index={opt.value}
                                    disabled={false}
                                    func={() => onToggleLienPosition(opt.value)}
                                    leftIcon={null}
                                    rightIcon={null}
                                />
                            ))}
                        </div>
                        <span className="body-bold colour-primary leads-filter-section-label">
                            Auction date
                        </span>
                        <div className="leads-filter-row">
                            <Input
                                label={null}
                                labelType={null}
                                info={false}
                                value={auctionDateMin}
                                onChange={(e) => setAuctionDateMin(e.target.value)}
                                placeholder="From"
                                type="date"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                                submitPromptDisabled={false}
                                submitPrompt={null}
                                onHeightChange={null}
                            />
                            <span className="body-regular colour-secondary leads-filter-dash">–</span>
                            <Input
                                label={null}
                                labelType={null}
                                info={false}
                                value={auctionDateMax}
                                onChange={(e) => setAuctionDateMax(e.target.value)}
                                placeholder="To"
                                type="date"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                                submitPromptDisabled={false}
                                submitPrompt={null}
                                onHeightChange={null}
                            />
                        </div>
                    </div>
                </>
            )}

            <div className="light-grey-divider"></div>

            {/* Property Details Section */}
            <div className="leads-filter-section">
                <span className="body-bold colour-primary leads-filter-section-label">
                    Property details
                </span>
                {availablePropertyTypes.length > 0 && (
                    <div className="leads-filter-chip-row">
                        {availablePropertyTypes.map(pt => (
                            <FilterChip
                                key={pt}
                                label={pt.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                selected={selectedPropertyTypes.includes(pt)}
                                index={pt}
                                disabled={false}
                                func={() => onTogglePropertyType(pt)}
                                leftIcon={null}
                                rightIcon={null}
                            />
                        ))}
                    </div>
                )}
                {renderRangeRow('Beds', bedsMin, setBedsMin, bedsMax, setBedsMax, 'Min', null)}
                {renderRangeRow('Baths', bathsMin, setBathsMin, bathsMax, setBathsMax, 'Min', null)}
                {renderRangeRow('Sq Ft', sqftMin, setSqftMin, sqftMax, setSqftMax, 'Min', null)}
                {renderRangeRow('Year Built', yearBuiltMin, setYearBuiltMin, yearBuiltMax, setYearBuiltMax, 'Min', null)}
            </div>

        </div>
    );
}

export default LeadsFilterModal;
